import type { FeatureFlagValueType } from '@brightdrop/feature-flags-client';
import type { NavBarItem } from '@gm-commercial/navbar-model';
import type { Profile } from '@gm-commercial/profile-model';

import NotificationIcon from '~/assets/icons/new/notification.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  SETTINGS_NOTIFICATIONS_ACCOUNT,
  SETTINGS_NOTIFICATIONS_VEHICLES,
} from '~/common/models/pages/settingsPages.model';
import { RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';

const generateTestId = (
  base: string,
  value: string,
  suffix = 'nav-list-item'
) => {
  return `${base}-${value}-${suffix}`;
};

export const getNotificationNavBarItems = ({
  profileParams,
  findFlagScope,
  showTelematicsFleetNavSurfaces = true,
  hasEmerNotificationsAccess = false,
}: {
  profileParams: Profile['permissionsContextIds'];
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
  showTelematicsFleetNavSurfaces?: boolean;
  /** Whether the org can set up emergency notifications (EMER flag and Protect). */
  hasEmerNotificationsAccess?: boolean;
}): NavBarItem[] => {
  // Safety & Security holds the driving and camera notifications for telematics
  // accounts, and the emergency notifications for Protect accounts.
  const isDisplaySafetyAndSecurity =
    Boolean(showTelematicsFleetNavSurfaces) || hasEmerNotificationsAccess;

  const context = {
    fleetsId: profileParams?.fleetsId || undefined,
    hubsId: profileParams?.hubsId || undefined,
    organizationsId: profileParams?.organizationsId || undefined,
  };

  const accountItem = {
    label: translateText('common:account'),
    testId: generateTestId('account-notifications', 'account'),
    path: SETTINGS_NOTIFICATIONS_ACCOUNT,
    matchNestedPaths: true,
  };

  const vehiclesItem = {
    label: translateText('common:vehicles'),
    testId: generateTestId('account-notifications', 'vehicles'),
    path: SETTINGS_NOTIFICATIONS_VEHICLES,
    matchNestedPaths: true,
  };

  const safetyAndSecurityItem = {
    label: translateText('common:safetyAndSecurity'),
    testId: generateTestId('account-notifications', 'safety-and-security'),
    path: makeRoutePath(
      findRoute(
        RouteName.CS_PLATFORM_SAFETY_AND_SECURITY_NOTIFICATIONS_SETTINGS
      ),
      context,
      true,
      true,
      findFlagScope
    ),
    matchNestedPaths: true,
  };
  // Order: Account first, then Vehicle and then Safety and Security (if conditions are met)
  const children = [
    accountItem,
    vehiclesItem,
    ...(isDisplaySafetyAndSecurity ? [safetyAndSecurityItem] : []),
  ].filter((item) => item.path !== '');

  if (children.length === 0) {
    return [];
  }

  return [
    {
      label: translateText('settings:notifications.title'),
      icon: <NotificationIcon className="noFill" width={24} height={24} />,
      testId: generateTestId('account', 'notifications', 'header'),
      path: SETTINGS_NOTIFICATIONS_ACCOUNT,
      children,
    },
  ];
};
